import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, CreditCard, ArrowRight } from 'lucide-react';
import { useSiteContent } from '../content/SiteContentProvider';
import { setCookieConsentChoice, useCookieConsent } from '../lib/cookieConsent';

export default function InfoAndMap() {
  const { content } = useSiteContent();
  const { acceptsOptionalCookies } = useCookieConsent();

  return (
    <section className="relative w-full flex flex-col lg:flex-row z-20 overflow-hidden">
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] transform rotate-180 z-30 opacity-20 pointer-events-none mix-blend-overlay">
          <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-[150%] h-[40px] ml-[-25%]">
              <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" fill="#000000"></path>
          </svg>
      </div>

      <div className="w-full lg:w-1/2 bg-yellow-400 px-8 py-16 lg:p-20 text-[#1a1b24] flex flex-col justify-center relative">
         <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter mb-12 relative z-10">
          INFORMATIONS PRATIQUES
         </h2>

         <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-8 mb-12 relative z-10">
            <div>
              <h3 className="font-extrabold text-lg mb-6 uppercase flex items-center gap-2 tracking-wide">
                <Clock size={22} strokeWidth={2.5} />
                <span>HORAIRES <br/>D'OUVERTURE</span>
              </h3>
              <ul className="space-y-1.5 font-medium text-sm md:text-base">
                {content.weeklyHours.map(({ day, time }) => (
                  <li key={day}>{day.toUpperCase()} : <strong>{time}</strong></li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-extrabold text-lg mb-6 uppercase tracking-wide leading-tight">
                LIENS RAPIDES
              </h3>
              <div className="flex flex-col gap-3">
                <Link to="/nos-activites/action-game" className="font-bold hover:text-white transition-colors uppercase text-sm tracking-wide">Action Game</Link>
                <Link to="/entreprises/team-building" className="font-bold hover:text-white transition-colors uppercase text-sm tracking-wide">Team Building</Link>
                <Link to="/entreprises/privatisation" className="font-bold hover:text-white transition-colors uppercase text-sm tracking-wide">Privatisation</Link>
                <Link to="/entreprises/seminaire" className="font-bold hover:text-white transition-colors uppercase text-sm tracking-wide">Séminaire</Link>
                <Link to="/formules/anniversaire-enfant" className="font-bold hover:text-white transition-colors uppercase text-sm tracking-wide">Anniversaires</Link>
                <Link to="/contact" className="font-bold hover:text-white transition-colors uppercase text-sm tracking-wide">Contact</Link>
              </div>
            </div>
         </div>

         <div className="relative z-10 max-w-md">
            <div>
              <h3 className="font-extrabold text-lg mb-6 uppercase flex items-center gap-2 tracking-wide">
                <CreditCard size={22} className="text-[#1a1b24]" strokeWidth={2.5} />
                PAIEMENT
              </h3>
              <div className="flex gap-3 flex-wrap items-center">
                <div className="bg-[#14235e] text-white px-3 py-1.5 rounded text-sm font-black italic shadow-sm tracking-widest leading-none">VISA</div>
                <div className="bg-[#1a1b24] text-white px-2 py-1.5 rounded flex items-center gap-1.5 shadow-sm">
                  <div className="flex -space-x-1.5 items-center">
                    <div className="w-3.5 h-3.5 rounded-full bg-red-500 mix-blend-screen isolate opacity-90"></div>
                    <div className="w-3.5 h-3.5 rounded-full bg-yellow-500 mix-blend-screen isolate opacity-90"></div>
                  </div>
                  <span className="text-[10px] font-bold lowercase tracking-wide mt-0.5" style={{fontVariant: "small-caps"}}>Mastercard</span>
                </div>
                <div className="bg-[#004f9f] text-white px-3 py-1.5 rounded flex items-center shadow-sm">
                  <span className="text-[11px] font-bold tracking-wider leading-none mt-0.5">CHÈQUE-VACANCES</span>
                </div>
              </div>
            </div>
         </div>
      </div>

      <div className="w-full lg:w-1/2 bg-yellow-400 p-6 sm:p-12 lg:p-20 flex items-center justify-center">
         <div className="w-full max-w-lg shadow-[0_20px_50px_rgba(0,0,0,0.3)] rounded-2xl overflow-hidden flex flex-col transform group transition-transform duration-500 hover:-translate-y-2">
           <div className="w-full h-72 sm:h-80 lg:h-96 relative bg-gray-200">
             {acceptsOptionalCookies ? (
               <iframe 
                 src={content.mapEmbedUrl}
                 title="Carte Google Maps Gameside Caen à Ifs"
                 width="100%" 
                 height="100%" 
                 style={{ border: 0 }} 
                 allowFullScreen={false} 
                 loading="lazy" 
                 referrerPolicy="no-referrer-when-downgrade" 
                 className="absolute inset-0 transition-all duration-700 object-cover"
               ></iframe>
             ) : (
               <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#f2f3f6] p-6 text-center">
                 <p className="text-[#212330] font-black uppercase tracking-wide">
                   Carte Google Maps désactivée
                 </p>
                 <p className="max-w-sm text-sm font-medium leading-relaxed text-gray-600">
                   Acceptez les cookies optionnels pour afficher la carte interactive.
                 </p>
                 <button
                   type="button"
                   onClick={() => setCookieConsentChoice('accepted')}
                   className="rounded-full bg-yellow-400 px-5 py-3 text-sm font-black uppercase tracking-wide text-[#212330]"
                 >
                   Accepter et afficher
                 </button>
               </div>
             )}
           </div>
           <div className="bg-[#212330] p-6 lg:p-8 text-white flex flex-col sm:flex-row items-center justify-between gap-6 relative z-20">
             <div>
                <h3 className="font-bold text-sm mb-1 uppercase tracking-widest text-gray-400 text-center sm:text-left">
                  ADRESSE
                </h3>
                <a href={content.directionsUrl} target="_blank" rel="noreferrer" className="block font-semibold text-lg text-center sm:text-left leading-tight hover:text-yellow-400 transition-colors">
                  {content.addressLines[0]}<br/>
                  {content.addressLines[1]}
                </a>
             </div>
             <a href={content.directionsUrl} target="_blank" rel="noreferrer" className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-[#1a1b24] font-black px-6 py-3.5 rounded-full flex items-center justify-center gap-2 transform hover:scale-105 transition-all shadow-lg uppercase tracking-wide shrink-0">
                Y ALLER <ArrowRight size={18} strokeWidth={3} />
             </a>
           </div>
         </div>
      </div>

    </section>
  );
}
